"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const filterChips = [{
  label: "LIVE STREAM",
  value: "live-stream"
}, {
  label: "VR IMMERSIVE",
  value: "vr-immersive"
}, {
  label: "CONCERT",
  value: "concert"
}, {
  label: "NIGHTCLUB",
  value: "nightclub"
}, {
  label: "ART SHOW",
  value: "art-show"
}, {
  label: "SPORTING",
  value: "sporting"
}];
function AmplifierGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[3/5] bg-secondary/40 backdrop-blur-md border border-border animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="py-16 text-center font-mono text-sm text-muted-foreground">
        <span>TRANSMISSION INTERRUPTED. </span>
        <Button variant="link" onClick={refetch} className="font-mono text-primary p-0 h-auto underline">
          RETRY SIGNAL
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="py-16 text-center font-mono text-sm text-muted-foreground">
        THE VAULT IS CURRENTLY SEALED.
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => {
      const isNew = product.tags?.some(t => t.name?.toLowerCase().includes("new"));
      const isLimited = product.tags?.some(t => t.name?.toLowerCase().includes("limited"));
      const badgeText = isLimited ? "EXCLUSIVE" : isNew ? "LIVE NOW" : null;
      const sku = `COI-${String(product.id).padStart(3, "0")}`;
      const categoryLabel = product.categories?.[0]?.name?.toUpperCase() || "PREMIUM EVENT ACCESS";
      const power = product.customFields?.power || "4K / 60FPS";
      const tubes = product.customFields?.tubes || "VR ENABLED";
      const weight = product.customFields?.weight || "UNLIMITED";
      return <article data-index={i} key={product.id} className="group relative flex flex-col bg-card/60 backdrop-blur-md border border-border shadow-2xl overflow-hidden transition-all duration-300 hover:shadow-4xl hover:border-primary/60" style={{
        borderRadius: "0px"
      }}>
            {}
            <span className="pointer-events-none absolute top-0 left-0 h-px w-0 bg-primary transition-all duration-500 group-hover:w-full" />

            {}
            <div className="relative aspect-[3/4] overflow-hidden bg-background">
              <Link to={product.link}>
                <img src={product.featured_image_url || `/img/event_preview_${i}.webp`} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] grayscale-[20%] group-hover:grayscale-0" />
              </Link>

              {}
              <span className="absolute top-3 left-3 font-mono text-[11px] tracking-wider text-foreground bg-background/70 backdrop-blur-sm border border-border px-2 py-1">
                {sku}
              </span>

              {}
              {badgeText && <span className="absolute top-3 right-3 font-mono text-[11px] tracking-wider text-primary-foreground bg-destructive px-2 py-1 animate-pulse">
                  {badgeText}
                </span>}

              {}
              <span className="pointer-events-none absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] transition-opacity duration-500 group-hover:opacity-10" />
            </div>

            {}
            <div className="flex flex-col flex-1 p-6 gap-3">
              <span className="font-mono text-[11px] tracking-widest text-primary uppercase">
                {categoryLabel}
              </span>

              <Link to={product.link}>
                <h3 className="font-default text-xl font-bold text-foreground leading-tight tracking-tight hover:text-primary transition-colors">
                  {product.title}
                </h3>
              </Link>

              {}
              <ul className="flex flex-col mt-2">
                <li className="flex items-center justify-between border-t border-border py-2">
                  <span className="font-mono text-[11px] tracking-widest text-muted-foreground">
                    RESOLUTION
                  </span>
                  <span className="font-mono text-sm text-foreground">
                    {power}
                  </span>
                </li>
                <li className="flex items-center justify-between border-t border-border py-2">
                  <span className="font-mono text-[11px] tracking-widest text-muted-foreground">
                    IMMERSION
                  </span>
                  <span className="font-mono text-sm text-foreground">
                    {tubes}
                  </span>
                </li>
                <li className="flex items-center justify-between border-t border-b border-border py-2">
                  <span className="font-mono text-[11px] tracking-widest text-muted-foreground">
                    ACCESS
                  </span>
                  <span className="font-mono text-sm text-foreground">
                    {weight}
                  </span>
                </li>
              </ul>

              {}
              <div className="flex items-center justify-between mt-3">
                <span className="font-mono text-2xl text-foreground tracking-tight">
                  ${product.price?.toLocaleString?.() ?? product.price}
                </span>
                <Link to={product.link}>
                  <Button variant="outline" size="sm" className="font-mono text-[11px] tracking-widest border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary" style={{
                borderRadius: "0px"
              }}>
                    VIEW FEED →
                  </Button>
                </Link>
              </div>

              {}
              <div className="mt-2">
                <AddToCartButton productId={product.id} className="w-full" buttonClassName="bg-primary text-primary-foreground font-mono text-[11px] tracking-widest hover:bg-primary/90" text="INITIATE ACCESS" showQuantity={false} />
              </div>
            </div>
          </article>;
    })}
    </div>;
}
function FilterToolbar() {
  const {
    setSorting
  } = useEcommerceProducts();
  const [activeChip, setActiveChip] = React.useState<string | null>(null);
  const handleSort = (value: string) => {
    if (value === "newest") setSorting("date", "desc");else if (value === "oldest") setSorting("date", "asc");else if (value === "price-low") setSorting("price", "asc");else if (value === "price-high") setSorting("price", "desc");else if (value === "name") setSorting("title", "asc");
  };
  return <div className="flex flex-col lg:flex-row lg:items-center lg:justify-end gap-4">
      <div className="flex flex-wrap gap-2">
        {filterChips.map((chip, i) => <Button key={chip.value} data-index={i} variant="outline" size="sm" onClick={() => setActiveChip(activeChip === chip.value ? null : chip.value)} className={activeChip === chip.value ? "font-mono text-[11px] tracking-widest h-8 px-3 bg-primary text-primary-foreground border-primary hover:bg-primary/90" : "font-mono text-[11px] tracking-widest h-8 px-3 bg-transparent text-foreground border-border hover:bg-primary hover:text-primary-foreground hover:border-primary"} style={{
        borderRadius: "0px"
      }}>
            {chip.label}
          </Button>)}
      </div>

      <Select defaultValue="newest" onValueChange={handleSort}>
        <SelectTrigger className="font-mono text-[11px] tracking-widest h-8 px-3 bg-transparent text-foreground border-border w-auto" style={{
        borderRadius: "0px"
      }}>
          <span className="text-muted-foreground mr-1">FILTER:</span>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-popover border-border" style={{
        borderRadius: "0px"
      }}>
          <SelectItem value="newest" className="font-mono text-xs tracking-wider">
            LATEST RELEASES
          </SelectItem>
          <SelectItem value="oldest" className="font-mono text-xs tracking-wider">
            ARCHIVED RITUALS
          </SelectItem>
          <SelectItem value="price-low" className="font-mono text-xs tracking-wider">
            PRICE: LOW → HIGH
          </SelectItem>
          <SelectItem value="price-high" className="font-mono text-xs tracking-wider">
            PRICE: HIGH → LOW
          </SelectItem>
          <SelectItem value="name" className="font-mono text-xs tracking-wider">
            ALPHABETICAL
          </SelectItem>
        </SelectContent>
      </Select>
    </div>;
}
import { Link } from "@/components/common/Link";
export default function LiveFromTheAltar() {
  const wp_query_params = {
    per_page: 9,
    post_type: "product"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="2231" id="live-from-the-altar" className="bg-background text-foreground py-[120px] px-6 md:px-12">
      <EcommerceProductsProvider wp_query={{
      per_page: 9,
      post_type: "product"
    }}>
        <div className="max-w-[1400px] mx-auto">
          {}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div className="flex flex-col gap-3">
              <span className="font-mono text-[11px] tracking-[0.2em] text-primary uppercase">
                — 01 / THE VAULT
              </span>
              <h2 className="font-default font-bold text-foreground tracking-tight leading-none text-[2.5rem] md:text-[3.5rem]">
                LIVE FROM THE ALTAR
              </h2>
            </div>

            <FilterToolbar />
          </div>

          {}
          <div className="w-full h-px bg-border mt-10 mb-10" />

          {}
          <AmplifierGridConsumer />
        </div>
      </EcommerceProductsProvider>
    </section>;
}